// TikTok Pixel helper — emits standard events with deduplication support.
// Pairs with backend Events API so each event has a stable event_id used by both sides for dedup.
// Also identifies the user via ttq.identify() once we know their email — boosts EMQ.

import { api } from "./api";

const CONTENTS = {
  pdf: {
    content_id: "bluebook_pdf",
    content_type: "product",
    content_name: "الكتاب الأزرق - PDF",
    price: 27,
  },
  pdf_audio: {
    content_id: "bluebook_pdf_audio",
    content_type: "product",
    content_name: "الكتاب الأزرق - PDF + النسخة الصوتية",
    price: 41,
  },
  pink_audio: {
    content_id: "bluebook_pink_audio",
    content_type: "product",
    content_name: "الكتاب الوردي - البرنامج الصوتي",
    price: 39.73,
  },
  pdf_audio_eg: {
    content_id: "bluebook_pdf_audio_eg",
    content_type: "product",
    content_name: "الكتاب الأزرق - النسخة المصرية",
    price: 9.55,
  },
  pdf_audio_eg_exit: {
    content_id: "bluebook_pdf_audio_eg_exit",
    content_type: "product",
    content_name: "الكتاب الأزرق - عرض المصري",
    price: 7.65,
  },
  pdf_audio_exit5: {
    content_id: "bluebook_pdf_audio_exit5",
    content_type: "product",
    content_name: "الكتاب الأزرق - عرض الاستراد",
    price: 22,
  },
  ai_work_upgrade: {
    content_id: "ai_work_upgrade",
    content_type: "product",
    content_name: "AI Work Upgrade",
    price: 79,
    currency: "USD",
  },
  thabit_audio: {
    content_id: "thabit_audio",
    content_type: "product",
    content_name: "ثابت — جلسات صوتية",
    price: 99,
    currency: "SAR",
  },
  reply_rescue_5credits: {
    content_id: "reply_rescue_5credits",
    content_type: "product",
    content_name: "Reply Rescue — Rescue Session (5 Credits)",
    price: 9.99,
    currency: "USD",
  },
  reply_rescue_15credits: {
    content_id: "reply_rescue_15credits",
    content_type: "product",
    content_name: "Reply Rescue — Deep Rescue (15 Credits)",
    price: 19.99,
    currency: "USD",
  },
  reply_rescue_24h_pass: {
    content_id: "reply_rescue_24h_pass",
    content_type: "product",
    content_name: "Reply Rescue — 24h Unlimited Pass",
    price: 14.99,
    currency: "USD",
  },
  ipo_access_dossier: {
    content_id: "ipo_access_dossier",
    content_type: "product",
    content_name: "IPO Access Dossier™",
    price: 79,
    currency: "USD",
  },
};

// In-memory + sessionStorage cache so identifiers persist through page navigations.
const IDENT_KEY = "bb_ttk_ident";

function loadIdent() {
  try {
    const raw = sessionStorage.getItem(IDENT_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) { /* sessionStorage may be unavailable (SSR/private mode) */ }
  return {};
}

function saveIdent(ident) {
  try {
    sessionStorage.setItem(IDENT_KEY, JSON.stringify(ident));
  } catch (_) { /* sessionStorage may be unavailable (SSR/private mode) */ }
}

// Hash + identify the user with TikTok Pixel once we know their email.
// ttq.identify expects already-hashed identifiers OR plain values (it hashes internally).
export function identifyUser({ email, phone, externalId } = {}) {
  if (typeof window === "undefined") return;
  const ident = loadIdent();
  if (email) ident.email = email.trim().toLowerCase();
  if (phone) ident.phone = phone.trim();
  if (externalId) ident.external_id = externalId;
  saveIdent(ident);
  try {
    if (window.ttq && window.ttq.identify) {
      const payload = {};
      if (ident.email) payload.email = ident.email;
      if (ident.phone) payload.phone_number = ident.phone;
      if (ident.external_id) payload.external_id = ident.external_id;
      if (Object.keys(payload).length) window.ttq.identify(payload);
    }
  } catch (_) { /* sessionStorage may be unavailable (SSR/private mode) */ }
}

function getIdent() {
  return loadIdent();
}

function newEventId() {
  return `ev_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function pixelTrack(eventName, params, eventId) {
  if (typeof window === "undefined" || !window.ttq) return;
  try {
    window.ttq.track(eventName, params, { event_id: eventId });
  } catch (_) { /* sessionStorage may be unavailable (SSR/private mode) */ }
}

function buildContents(pkg) {
  const c = CONTENTS[pkg];
  if (!c) return null;
  return {
    contents: [
      {
        content_id: c.content_id,
        content_type: c.content_type,
        content_name: c.content_name,
        quantity: 1,
        price: c.price,
      },
    ],
    value: c.price,
    // Per-product currency override (e.g. SAR for thabit/ai) — legacy
    // products without an explicit currency default to USD.
    currency: c.currency || "USD",
  };
}

// Fire-and-forget server postback (Events API) — never blocks UI.
// Always include any known identifiers so server-side hashing can occur.
function serverEvent(eventName, payload) {
  const ident = getIdent();
  api
    .post("/tiktok/event", {
      event_name: eventName,
      email: ident.email,
      phone: ident.phone,
      ...payload,
    })
    .catch(() => {});
}

export function trackViewContent(pkg = "pdf") {
  const body = buildContents(pkg);
  if (!body) return;
  const eventId = newEventId();
  pixelTrack("ViewContent", body, eventId);
  serverEvent("ViewContent", { event_id: eventId, ...body });
}

export function trackAddToCart(pkg = "pdf") {
  const body = buildContents(pkg);
  if (!body) return;
  const eventId = newEventId();
  pixelTrack("AddToCart", body, eventId);
  serverEvent("AddToCart", { event_id: eventId, ...body });
}

export function trackClickButton(label = "cta") {
  const eventId = newEventId();
  pixelTrack("ClickButton", { description: label }, eventId);
}

export function trackInitiateCheckout(pkg) {
  const body = buildContents(pkg);
  if (!body) return;
  const eventId = newEventId();
  pixelTrack("InitiateCheckout", body, eventId);
  serverEvent("InitiateCheckout", { event_id: eventId, ...body });
}

export function trackAddPaymentInfo(pkg) {
  const body = buildContents(pkg);
  if (!body) return;
  const eventId = newEventId();
  pixelTrack("AddPaymentInfo", body, eventId);
  serverEvent("AddPaymentInfo", { event_id: eventId, ...body });
}

export function trackPurchase(orderId, pkg) {
  const body = buildContents(pkg);
  if (!body) return;
  const eventId = `purchase_${orderId}`; // deterministic dedup with backend webhook
  pixelTrack("Purchase", body, eventId);
  // Backend already fires this from Stripe webhook; this browser-side call
  // gives fastest attribution. Server side dedups via the same event_id.
}
